import * as c from 'constants/castTypes';
import * as f from 'utils/fieldConstants';
import * as t from 'utils/fieldTypes';
import { ComparatorsDropdownProps } from './types';
import { withFeatureFlaggedComparators, withMultiSelectInputComparators } from './utils';

/**
 * getOptionsForCastType
 * Determines the list of comparators based on the cast type.
 * @param { String } type: The field type.
 * @returns { Array } List of comparators.
 */

export const getOptionsForCastType = (
  type: string,
  comparatorDropdownProps: ComparatorsDropdownProps,
  field?: string,
) => {
  const { numericTypeComparators, defaultComparators } = comparatorDropdownProps;

  if (type === t.DOUBLE || type === t.LONG) {
    switch (field) {
      case f.CONTENT_ID:
      case f.MESSAGE_TYPE:
      case f.MESSAGE_CHANNEL:
      case f.UNSUBSCRIBED_CHANNEL:
      case f.UNSUBSCRIBED_CHANNELS:
      case f.RECURRING_CAMPAIGN:
      case f.CAMPAIGN:
      case f.SUBSCRIBED_MESSAGE_TYPES:
      case f.UNSUBSCRIBED_MESSAGE_TYPES:
      case f.UNSUBSCRIBED_MESSAGE_TYPE:
      case f.HREF_INDEX:
        return numericTypeComparators;
      default:
        return [
          ...numericTypeComparators,
          ...withMultiSelectInputComparators(comparatorDropdownProps),
        ];
    }
  }
  if (type === c.EPOCHS_SECS) {
    return [];
  }

  return withFeatureFlaggedComparators(defaultComparators, comparatorDropdownProps);
};

/**
 * getOptionsForField
 * Determines the list of comparators based on the selected field.
 * @param { String } field: The field value.
 * @param { String } type: The field type.
 * @returns { Array } List of comparators.
 */
export const getOptionsForField = (
  field: string,
  type: string,
  comparatorDropdownProps: ComparatorsDropdownProps,
) => {
  const { brandAffinityComparators, listTypeComparators, campaignLabelsComparators } =
    comparatorDropdownProps;

  switch (field) {
    case f.CANONICAL_URL:
    case f.LIST_MEMBERSHIP_SINGLE:
    case f.USER_LIST_MEMBERSHIP_SINGLE:
    case f.TEMPLATE_ID:
      return listTypeComparators;
    case f.LIST_MEMBERSHIP:
    case f.USER_LIST_MEMBERSHIP:
      return withFeatureFlaggedComparators(listTypeComparators, comparatorDropdownProps, field);
    case f.CAMPAIGN_LABEL:
      return campaignLabelsComparators;
    case f.BRAND_AFFINITY:
      return brandAffinityComparators;
    default:
      return getOptionsForCastType(type, comparatorDropdownProps, field);
  }
};

/**
 * getOptionsForType
 * Determines the list of comparators based on the selected type.
 * @param { String } type: The field type.
 * @param { String } field: The field value.
 * @returns { Array } List of comparators.
 */
export const getOptionsForType = (
  type: string,
  field: string,
  comparatorDropdownProps: ComparatorsDropdownProps,
) => {
  const {
    booleanTypeComparators,
    dateTypeComparators,
    geoLocationTypeComparators,
    objectTypeComparators,
    stringTypeComparators,
    defaultComparators,
  } = comparatorDropdownProps;

  if (field === f.BRAND_AFFINITY) return getOptionsForField(field, type, comparatorDropdownProps);
  if (field === f.PHONE_NUMBER_DETAILS) return getOptionsForCastType(type, comparatorDropdownProps);
  if (field === f.MESSAGE_ID) return stringTypeComparators;

  switch (type) {
    case t.BOOLEAN:
      return booleanTypeComparators;
    case t.DATE:
      return dateTypeComparators;
    case t.GEO_LOCATION:
      return geoLocationTypeComparators;
    case t.OBJECT:
      return objectTypeComparators;
    case t.STRING:
      return withFeatureFlaggedComparators(stringTypeComparators, comparatorDropdownProps);
    case t.NESTED:
      return defaultComparators;
    default:
      return getOptionsForField(field, type, comparatorDropdownProps);
  }
};
